// Surface lighting of the painted height field. Pigment colour (albedo) is an
// input and is never modified by lighting, so lighting can change freely.
//
// Shading model per cell:
//   normal     from central differences of the elevation field
//   diffuse    Lambert, times a soft height-field shadow found by marching
//              toward the light and comparing the terrain with the light ray
//   ambient    hemispherical environment term times a cavity occlusion
//              estimate (how far the cell sits below its neighbourhood)
//   specular   GGX microfacet distribution, Schlick Fresnel (F0 = 0.04 for
//              paint films and water, n ~ 1.5 / 1.33), Smith-Schlick geometry;
//              roughness from the cell's gloss (wet paint is smooth)
// The light colour comes from a black-body temperature, relative to 6500 K.
// LIGHTING_FRAGMENT_SHADER mirrors lightCell() for the WebGL2 renderer.

import { blackbodyLinearRgb } from '../pigments/ColourConversion.js';

export const DEFAULT_LIGHT = Object.freeze({
  azimuth: 135, // degrees, direction the light comes FROM (0 = from +x, 90 = from +y/bottom)
  elevation: 35, // degrees above the canvas plane
  intensity: 1.0,
  temperature: 5600,
  ambient: 0.32,
  specular: 0.6,
  roughness: 1.0, // multiplier on surface roughness
  shadowSoftness: 0.35,
  depthScale: 1.0, // impasto depth appearance
  view: 'shaded', // shaded | pigment | height | wetness | normals
});

export function lightColour(temperature) {
  const c = blackbodyLinearRgb(temperature);
  const ref = blackbodyLinearRgb(6500);
  const rgb = [c[0] / ref[0], c[1] / ref[1], c[2] / ref[2]];
  const max = Math.max(rgb[0], rgb[1], rgb[2]);
  return rgb.map((v) => v / max);
}

export function lightVector(light) {
  const az = (light.azimuth * Math.PI) / 180;
  const el = (Math.max(2, Math.min(90, light.elevation)) * Math.PI) / 180;
  return [Math.cos(el) * Math.cos(az), Math.cos(el) * Math.sin(az), Math.sin(el)];
}

function sampleClamped(field, width, height, x, y) {
  const xi = x < 0 ? 0 : x >= width ? width - 1 : x | 0;
  const yi = y < 0 ? 0 : y >= height ? height - 1 : y | 0;
  return field[yi * width + xi];
}

const SHADOW_STEPS = 12;

export function lightCell(buffers, x, y, light, L, colour, out) {
  const { width, height, albedo, elevation, gloss } = buffers;
  const i = y * width + x;
  const ar = albedo[i * 3];
  const ag = albedo[i * 3 + 1];
  const ab = albedo[i * 3 + 2];
  const depth = light.depthScale;
  const h = elevation[i] * depth;
  // Sobel gradient: less sensitive to single-cell spikes than central differences.
  const e = (dx, dy) => sampleClamped(elevation, width, height, x + dx, y + dy);
  const gx = (e(1, -1) + 2 * e(1, 0) + e(1, 1) - e(-1, -1) - 2 * e(-1, 0) - e(-1, 1)) / 8;
  const gy = (e(-1, 1) + 2 * e(0, 1) + e(1, 1) - e(-1, -1) - 2 * e(0, -1) - e(1, -1)) / 8;
  let nx = -gx * depth;
  let ny = -gy * depth;
  let nz = 1;
  const nl = Math.hypot(nx, ny, nz);
  nx /= nl;
  ny /= nl;
  nz /= nl;
  const ndl = Math.max(0, nx * L[0] + ny * L[1] + nz * L[2]);

  // Soft shadow: march toward the light; terrain above the ray occludes.
  let shadow = 1;
  const horiz = Math.hypot(L[0], L[1]);
  if (horiz > 1e-3 && ndl > 0) {
    const dx = L[0] / horiz;
    const dy = L[1] / horiz;
    const rise = L[2] / horiz;
    let occlusion = 0;
    for (let s = 1; s <= SHADOW_STEPS; s += 1) {
      const t = s * 1.5;
      const terrain = sampleClamped(elevation, width, height, x + dx * t, y + dy * t) * depth;
      const ray = h + rise * t;
      const above = (terrain - ray) / (light.shadowSoftness * t + 0.05);
      if (above > occlusion) occlusion = above;
    }
    shadow = 1 - Math.min(1, occlusion);
  }

  // Cavity occlusion from the 4-neighbourhood at radius 2.
  const ring = (sampleClamped(elevation, width, height, x - 2, y) + sampleClamped(elevation, width, height, x + 2, y)
    + sampleClamped(elevation, width, height, x, y - 2) + sampleClamped(elevation, width, height, x, y + 2)) * 0.25 * depth;
  const ao = Math.max(0.4, Math.min(1, 1 - (ring - h) * 0.6));

  // GGX specular, view straight down (0, 0, 1).
  let hx = L[0];
  let hy = L[1];
  let hz = L[2] + 1;
  const hlen = Math.hypot(hx, hy, hz);
  hx /= hlen;
  hy /= hlen;
  hz /= hlen;
  const ndh = Math.max(0, nx * hx + ny * hy + nz * hz);
  const rough = Math.max(0.14, Math.min(1, (1 - gloss[i]) * light.roughness));
  const alpha = rough * rough;
  const a2 = alpha * alpha;
  const denom = ndh * ndh * (a2 - 1) + 1;
  const D = a2 / (Math.PI * denom * denom);
  const vdh = Math.max(0, hz);
  const F = 0.04 + 0.96 * Math.pow(1 - vdh, 5);
  const k = alpha / 2;
  const ndv = Math.max(1e-3, nz);
  const G = (ndl / (ndl * (1 - k) + k)) * (ndv / (ndv * (1 - k) + k));
  const spec = ndl > 0 ? (D * F * G) / (4 * Math.max(1e-3, ndl) * ndv) * ndl * light.specular * shadow : 0;

  const direct = light.intensity * ndl * shadow;
  const amb = light.ambient * ao;
  out[0] = ar * (direct * colour[0] + amb) + spec * colour[0] * light.intensity;
  out[1] = ag * (direct * colour[1] + amb) + spec * colour[1] * light.intensity;
  out[2] = ab * (direct * colour[2] + amb) + spec * colour[2] * light.intensity;
  return out;
}

function toSrgb8(v) {
  const c = v <= 0 ? 0 : v >= 1 ? 1 : v;
  return Math.round((c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055) * 255);
}

// CPU reference renderer: writes an RGBA8 image (same size as the buffers).
export function renderLitImage(buffers, lightOptions = {}, out = null) {
  const light = { ...DEFAULT_LIGHT, ...lightOptions };
  const { width, height } = buffers;
  const image = out || new Uint8ClampedArray(width * height * 4);
  const L = lightVector(light);
  const colour = lightColour(light.temperature);
  // Exposure normalises a flat white surface lit at the current elevation to ~0.9.
  const exposure = 0.9 / (light.intensity * L[2] + light.ambient);
  const rgb = [0, 0, 0];
  let maxElevation = 1e-6;
  if (light.view === 'height') for (let i = 0; i < buffers.elevation.length; i += 1) maxElevation = Math.max(maxElevation, buffers.elevation[i]);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = y * width + x;
      const o = i * 4;
      if (light.view === 'pigment') {
        rgb[0] = buffers.albedo[i * 3];
        rgb[1] = buffers.albedo[i * 3 + 1];
        rgb[2] = buffers.albedo[i * 3 + 2];
        image[o] = toSrgb8(rgb[0]);
        image[o + 1] = toSrgb8(rgb[1]);
        image[o + 2] = toSrgb8(rgb[2]);
      } else if (light.view === 'height') {
        const v = Math.round((buffers.elevation[i] / maxElevation) * 255);
        image[o] = v;
        image[o + 1] = v;
        image[o + 2] = v;
      } else if (light.view === 'wetness') {
        image[o] = Math.round(255 * (1 - buffers.wetness[i]));
        image[o + 1] = Math.round(255 * (1 - buffers.wetness[i] * 0.6));
        image[o + 2] = 255;
      } else {
        lightCell(buffers, x, y, light, L, colour, rgb);
        image[o] = toSrgb8(rgb[0] * exposure);
        image[o + 1] = toSrgb8(rgb[1] * exposure);
        image[o + 2] = toSrgb8(rgb[2] * exposure);
      }
      image[o + 3] = 255;
    }
  }
  return image;
}
